import { useCallback, useEffect, useState } from 'react';

import { useCardBroadcast } from './cardBroadcast';
import {
  openSummaryToViewState,
  viewStateToTargets,
  type CardSummaryView,
  type CardViewState,
} from './cardViewState';

export interface CardViewController {
  /** Current collapsed-summary sub-mode. */
  readonly summaryView: CardSummaryView;
  /** The card's current 4-state view (derived from open + summaryView). */
  readonly viewState: CardViewState;
  /** Apply one of the four view states (drives open + summaryView). */
  readonly applyViewState: (state: CardViewState) => void;
}

/**
 * Shared 4-state view behavior for record cards (PTV2-1157) — used by both `CollapsibleCard` and
 * `ClinicalSection`. The caller owns the open/collapsed state (managed, controlled, or local) and
 * passes it in with a setter; this hook owns the collapsed-summary sub-mode, maps the four view
 * states to (open, summaryView), and adopts "set all" broadcasts from a host header.
 *
 * @param open         the card's current open state
 * @param setOpenState setter for the card's open state (managed/local — caller's concern)
 */
export function useCardViewController(open: boolean, setOpenState: (next: boolean) => void): CardViewController {
  const [summaryView, setSummaryView] = useState<CardSummaryView>('off');

  const applyViewState = useCallback(
    (state: CardViewState): void => {
      const target = viewStateToTargets(state);
      setOpenState(target.open);
      if (target.summaryView !== undefined) {
        setSummaryView(target.summaryView);
      }
    },
    [setOpenState],
  );

  const broadcast = useCardBroadcast();
  const broadcastNonce = broadcast?.nonce ?? 0;
  const broadcastState = broadcast?.state ?? 'edit';
  const broadcastForced = broadcast?.forced ?? false;
  useEffect(() => {
    if (broadcastNonce > 0) {
      applyViewState(broadcastState);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fire only when a new broadcast lands
  }, [broadcastNonce]);

  // Read-only (forced) mode: adopt the forced state on mount and whenever it changes, so a card that
  // mounts *after* the lock (e.g. selecting a node once already locked) is collapsed too — the nonce
  // path above only fires on a deliberate change and misses freshly-mounted cards. PTV2-1157.
  useEffect(() => {
    if (broadcastForced) {
      applyViewState(broadcastState);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fire on forced-state changes only
  }, [broadcastForced, broadcastState]);

  return { summaryView, viewState: openSummaryToViewState(open, summaryView), applyViewState };
}
